import { Link } from 'react-router-dom'
import { useReviewsAggregate } from '../../../hooks/useReviews'

const MANDALA_DOTS = [
  { cx: 100, cy: 10 },
  { cx: 184, cy: 70 },
  { cx: 16, cy: 130 },
  { cx: 150, cy: 168 },
  { cx: 50, cy: 32 },
  { cx: 186, cy: 118 },
  { cx: 14, cy: 82 },
]

export default function BookingHeroSection({ astro }) {
  // Same live-reviews source as ExpertProfileSummaryCard on this page — the
  // astrologer record's own rating/reviews_count aren't kept in sync
  // upstream. Shares that component's query cache (identical key), so this
  // doesn't add a second network request.
  const { summary } = useReviewsAggregate('astrologer', astro.id)
  const ratingLabel = summary.totalReviews > 0 ? summary.averageRating.toFixed(1) : 'New'

  return (
    <div className="relative overflow-hidden bg-primary text-white pt-28 pb-16">
      <div aria-hidden="true" className="absolute inset-0 bg-linear-to-b from-[#3a0a14] via-primary to-[#2a0710]" />
      <div aria-hidden="true" className="absolute inset-0 bg-[radial-gradient(55%_70%_at_50%_0%,rgba(232,119,15,0.20),transparent_70%)]" />
      <div aria-hidden="true" className="absolute inset-0 bg-[radial-gradient(45%_55%_at_100%_100%,rgba(232,119,15,0.10),transparent_70%)]" />

      <div
        aria-hidden="true"
        className="absolute -right-16 top-1/2 -translate-y-1/2 w-[24rem] h-96 opacity-[0.10] animate-[spin_140s_linear_infinite] motion-reduce:animate-none flex items-center justify-center pointer-events-none"
      >
        <span className="text-[170px] text-[#E6C878] select-none">ॐ</span>
      </div>
      <svg
        aria-hidden="true"
        className="absolute -right-8 top-1/2 -translate-y-1/2 w-md h-md opacity-[0.14] animate-[spin_260s_linear_infinite_reverse] motion-reduce:animate-none pointer-events-none"
        viewBox="0 0 200 200"
        fill="none"
        stroke="#E6C878"
      >
        <circle cx="100" cy="100" r="94" strokeWidth="0.4" />
        <circle cx="100" cy="100" r="10" strokeWidth="0.8" />
        <ellipse cx="100" cy="100" rx="90" ry="44" strokeWidth="0.5" />
        <ellipse cx="100" cy="100" rx="90" ry="44" strokeWidth="0.5" transform="rotate(60 100 100)" />
        <ellipse cx="100" cy="100" rx="90" ry="44" strokeWidth="0.5" transform="rotate(120 100 100)" />
        {MANDALA_DOTS.map((dot) => (
          <circle key={`${dot.cx}-${dot.cy}`} cx={dot.cx} cy={dot.cy} r="2.4" fill="#E6C878" stroke="none" fillOpacity="0.7" />
        ))}
      </svg>

      <div className="container px-6 mx-auto relative z-10">
        <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/40 mb-7">
          <Link to="/" className="hover:text-secondary transition-colors">Home</Link>
          <span className="opacity-40">/</span>
          <Link to={`/astrologers/${astro.id}`} className="hover:text-secondary transition-colors">{astro.name}</Link>
          <span className="opacity-40">/</span>
          <span className="text-secondary">Book</span>
        </div>

        <div className="flex flex-wrap items-end justify-between gap-8">
          <div className="max-w-xl">
            <div className="hero-chip mb-5 inline-flex">
              <span className="text-secondary">✦</span> Secure Booking
            </div>
            <h1 className="text-4xl md:text-5xl font-cinzel leading-tight">
              Book Your <span className="text-gradient-gold">Consultation</span>
            </h1>
            <p className="text-white/50 text-sm mt-3">Pick a slot below and confirm — it only takes a minute.</p>
          </div>

          <Link
            to={`/astrologers/${astro.id}`}
            className="hero-card-glass flex items-center gap-3 rounded-2xl px-4 py-3 hover:-translate-y-0.5 transition-transform"
          >
            <div className="w-11 h-11 rounded-full overflow-hidden shrink-0 border-2 border-secondary/40">
              {astro.image ? (
                <img
                  src={astro.image}
                  alt={astro.name}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = 'none' }}
                />
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center font-cinzel font-bold"
                  style={{ background: astro.fallbackGradient, color: 'rgba(120,50,0,.6)' }}
                >
                  {astro.fallbackLetter}
                </div>
              )}
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-[#1C140A] leading-tight">{astro.name}</p>
              <p className="text-[10px] text-[#5C3A18] mt-0.5 flex items-center gap-1.5">
                <span className="text-[#C8922A] font-bold">★ {ratingLabel}</span>
                <span className="opacity-40">·</span>
                <span className="truncate max-w-[10ch]">{astro.role}</span>
              </p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  )
}
